import _ from 'lodash';
import { createApp, defineComponent } from 'vue';
import ChartLinkPopup from '../chart/linkPopup/ChartLinkPopup.vue';
import rights from '../../../components/rights';
import globalToast from '$$uiKitComp/toast';

class Links {
  #instance;

  init(instance) {
    this.#instance = instance;
  }

  onLinkClick(id) {
    const link = this.#instance.getLink(id);

    if (!rights.project.hasRight(link.gantt_id, 'task_links')) {
      return false;
    }

    if (!this.#instance.config.auto_scheduling.enabled) {
      return this.showDeleteLinkPopup(id);
    }

    this.showLinkSettingsPopup(link);
  }

  onBeforeLinkAdd(id, link) {
    if (+link.source < 0 || +link.target < 0) {
      return false;
    }

    if (!this.#instance.isTaskExists(link.source) || !this.#instance.isTaskExists(link.target)) {
      return false;
    }

    if (
      this.#instance.getTask(link.target).type === this.#instance.config.types.button
      || !this.#instance.getTask(link.source).parent
      || !this.#instance.getTask(link.target).parent
    ) {
      return false;
    }

    if (this.#instance.getTask(link.target).gantt_id !== this.#instance.getTask(link.source).gantt_id) {
      globalToast.warning(__('gantt_msg_dependency_error'));

      return false;
    }

    link.gantt_id = link.gantt_id || this.#instance.getTask(link.target).gantt_id;
    link.lag = link.lag || 0;

    return true;
  }

  showLinkSettingsPopup(link) {
    const ComponentBuilder = defineComponent(ChartLinkPopup);
    const mountPoint = document.createElement('div');

    document.body.appendChild(mountPoint);

    const linkPopup = createApp(ComponentBuilder, {
      linkId: link.id,
      ganttId: link.gantt_id,
      instance: this.#instance,
      initialLag: this.getDurationViewValue(link, 'hour') * 60,
      onClose: () => {
        linkPopup.unmount();
        document.body.removeChild(mountPoint);
      }
    });

    linkPopup.mount(mountPoint);

    userExtAnalytics.log('gantt_link_click');
  }

  showDeleteLinkPopup(linkId) {
    webix.confirm({
      ok: __('common_yes'),
      cancel: __('common_no'),
      type: 'confirm-error',
      text: __('gantt_msg_link_delete_confirm'),
      callback: result => {
        if (result) {
          this.#instance.isLinkExists(linkId) && this.#instance.deleteLink(linkId);
        }
      },
    });
  }

  getDurationViewValue(link, durationUnit = this.#instance.config.duration_view) {
    const start = this.getStartAccordingToType(link);
    const end = this.getEndAccordingToType(link);

    return this.#instance.calculateDurationTo(start, end, durationUnit, link.gantt_id).duration;
  }

  getStartAccordingToType(link, allowedTypes) {
    let source = {};

    if (link) {
      source = this.#instance.getTask(link.source);


      for (const key in this.#instance.config.links) {
        if (this.#instance.config.links[key] === link.type) {
          if (allowedTypes && allowedTypes.indexOf(key) === -1) {
            return false;
          }

          switch (key) {
          case 'finish_to_start':
          case 'finish_to_finish':
            return source.end_date || this.#instance.calculateEndDate({
              start_date: source.start_date,
              duration: source.duration,
              task: source,
            });
          case 'start_to_start':
          case 'start_to_finish':
            return (new Date(source.start_date));
          default:
            return false;
          }
        }
      }
    }
  }

  getEndAccordingToType(link) {
    const target = this.#instance.getTask(link.target);

    for (const key in this.#instance.config.links) {
      if (this.#instance.config.links[key] === link.type) {
        switch (key) {
        case 'finish_to_start':
        case 'start_to_start':
          return target.start_date;
        case 'finish_to_finish':
        case 'start_to_finish':
          return target.end_date || this.#instance.calculateEndDate({
            start_date: target.start_date,
            duration: target.duration,
            task: target,
          });
        default:
          return false;
        }
      }
    }
  }

  customCalculateDuration(start, end) {
    return start.valueOf() > end.valueOf()
      ? -this.#instance.calculateDuration(end, start)
      : this.#instance.calculateDuration(start, end);
  }

  getTasksByWBS(ganttId, wbsItems) {
    const tasksData = {};

    this.#instance.eachTask(task => {
      if (task.gantt_id === ganttId && task.type !== this.#instance.config.types.button) {
        const wbs = this.#instance.getWBSCode(task);

        tasksData[wbs] = task;
      }
    });

    return wbsItems.reduce((result, wbsItem) => {
      const task = tasksData[wbsItem];

      if (task) {
        result.push(task)
      }

      return result;
    }, []);
  }

  predecessorColumnChange(taskId, wbsItems) {
    const task = this.#instance.getTask(taskId);

    const prevLinks = task.$target || [];
    const selectedTasks = this.getTasksByWBS(task.gantt_id, wbsItems);

    if (selectedTasks.length !== wbsItems.length) {
      globalToast.warning(__('gantt_msg_dependency_not_exist_error'));

      return false;
    }

    const insertLinks = selectedTasks.reduce((result, taskItem) => {
      const isExist = _.intersection(taskItem.$source, prevLinks);

      if (!isExist.length) {
        result.push({
          source: taskItem.id,
          target: task.id,
          gantt_id: task.gantt_id,
          type: '0',
          lag: 0,
        });
      }

      return result;
    }, []);

    const deleteLinks = prevLinks.reduce((result, linkId) => {
      const needDelete = !selectedTasks.find(item => item.$source.includes(linkId));

      if (needDelete) {
        result.push(this.#instance.getLink(linkId));
      }

      return result;
    }, []);

    const isWrongLink = insertLinks.find(insLink => {
      const source = this.#instance.getTask(insLink.source)
      const sourceTargets = source.$target.map(id => {
        const link = this.#instance.getLink(id);

        return link.source;
      });

      const isExist = sourceTargets.includes(task.id);

      return insLink.source === insLink.target || this.#instance.isCircularLink(insLink) || isExist;
    })

    if (isWrongLink) {
      globalToast.warning(__('gantt_msg_dependency_limit_error'));

      return false;
    }

    if (!insertLinks.length && !deleteLinks.length) {
      return false;
    }

    if (insertLinks.length) {
      userExtAnalytics.log('gantt_link_add_done', { from: 'chart' });

      insertLinks.forEach(link => {
        const type = Object.entries(this.#instance.config.links).find(([key, val]) => val === link.type)?.[0];

        userExtAnalytics.log('task_grid_action', { action: 'link_add', type });
      });
    }

    if (deleteLinks.length) {
      deleteLinks.forEach(link => {
        const type = Object.entries(this.#instance.config.links).find(([key, val]) => val === link.type)?.[0];

        userExtAnalytics.log('task_grid_action', { action: 'link_delete', type });
      });
    }

    this.#instance.callEvent('predecessorsUpdate', [task.id, insertLinks, deleteLinks]);

    return true;
  }
}

export default new Links();
